import React from 'react';
import { Calendar, DollarSign, Activity, Building, FileCheck, Quote, Star, Check } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { useTranslations } from '../hooks/useTranslations';
export function Requirements() {
  const {
    t,
    lang
  } = useTranslations();
  const requirements = [{
    icon: <Calendar className="w-5 h-5" data-id="element-381" />,
    text: t('requirements.req1.title')
  }, {
    icon: <DollarSign className="w-5 h-5" data-id="element-382" />,
    text: t('requirements.req2.title')
  }, {
    icon: <Activity className="w-5 h-5" data-id="element-383" />,
    text: t('requirements.req3.title')
  }, {
    icon: <Building className="w-5 h-5" data-id="element-384" />,
    text: t('requirements.req4.title')
  }, {
    icon: <FileCheck className="w-5 h-5" data-id="element-385" />,
    text: t('requirements.req5.title')
  }];
  const testimonials = lang === 'es' ? [{
    name: 'Carlos Méndez',
    role: 'Dueño de negocio – Servicios Generales',
    quote: 'El proceso fue mucho más rápido de lo que esperaba. Apliqué en la mañana y el mismo día ya tenía opciones claras. El equipo me explicó todo con paciencia y sin presiones.',
    stars: 5
  }, {
    name: 'María Fernanda Ruiz',
    role: 'Propietaria de restaurante',
    quote: 'Habíamos intentado con bancos tradicionales y siempre nos decían que no. En Brixen revisaron nuestros ingresos y nos ayudaron a conseguir capital para crecer.',
    stars: 5
  }, {
    name: 'José Ramírez',
    role: 'Empresa de construcción',
    quote: 'Lo que más me gustó fue la transparencia. Desde el inicio me dijeron qué opciones tenía y cuáles no. Nada de letras pequeñas.',
    stars: 5
  }, {
    name: 'Andrea López',
    role: 'E-commerce',
    quote: 'Necesitábamos capital para inventario y no sabíamos por dónde empezar. Brixen nos conectó con varias opciones y elegimos la que mejor se adaptaba a nuestro negocio.',
    stars: 5
  }, {
    name: 'Luis Hernández',
    role: 'Transporte y logística',
    quote: 'El acompañamiento fue clave. No solo fue aplicar y ya, sino que me guiaron durante todo el proceso hasta recibir los fondos.',
    stars: 5
  }] : [{
    name: 'Carlos Mendez',
    role: 'Business Owner – General Services',
    quote: 'The process was much faster than I expected. I applied in the morning and had clear options the same day. The team explained everything patiently and without pressure.',
    stars: 5
  }, {
    name: 'Maria Fernanda Ruiz',
    role: 'Restaurant Owner',
    quote: 'We had tried traditional banks and they always said no. At Brixen they reviewed our income and helped us get capital to grow.',
    stars: 5
  }, {
    name: 'Jose Ramirez',
    role: 'Construction Company',
    quote: "What I liked most was the transparency. From the start they told me what options I had and which ones I didn't. No fine print.",
    stars: 5
  }];
  return <section id="requirements" className="py-20 bg-gray-50" data-id="element-386">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-387">
        {/* Requirements Section */}
        <div className="mb-20" data-id="element-388">
          <div className="text-center mb-10" data-id="element-389">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-390">
              {t('requirements.title')}{' '}
              <span className="text-[#00E676]" data-id="element-391">
                {t('requirements.titleHighlight')}
              </span>
            </h2>
            <p className="text-gray-600 text-lg" data-id="element-392">
              {t('requirements.subtitle')}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-10" data-id="element-393">
            {requirements.map((req, index) => <div key={index} className="flex items-center bg-white px-6 py-4 rounded-lg shadow-sm border border-gray-100" data-id="element-394">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-395">
                  <Check className="w-4 h-4 text-[#00E676]" data-id="element-396" />
                </div>
                <span className="font-semibold text-gray-800" data-id="element-397">{req.text}</span>
              </div>)}
          </div>

          <div className="text-center" data-id="element-398">
            <Button size="lg" onClick={() => document.getElementById('contact')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-399">
              {t('requirements.cta')}
            </Button>
          </div>
        </div>

        {/* Testimonials Section */}
        <div id="testimonials" data-id="element-400">
          <div className="text-center mb-12" data-id="element-401">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-402">
              {t('testimonials.title')}{' '}
              <span className="text-[#00E676]" data-id="element-403">
                {t('testimonials.titleHighlight')}
              </span>
            </h2>
            <p className="text-gray-600" data-id="element-404">{t('testimonials.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-405">
            {testimonials.slice(0, lang === 'es' ? 5 : 3).map((testimonial, index) => <Card key={index} className="relative" data-id="element-406">
                  <Quote className="absolute top-6 right-6 w-8 h-8 text-green-100 fill-current" data-id="element-407" />
                  <div className="flex text-[#00E676] mb-4" data-id="element-408">
                    {[...Array(testimonial.stars)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-409" />)}
                  </div>
                  <p className="text-gray-600 mb-6 italic relative z-10 text-sm leading-relaxed" data-id="element-410">
                    "{testimonial.quote}"
                  </p>
                  <div data-id="element-411">
                    <p className="font-bold text-gray-900" data-id="element-412">
                      {testimonial.name}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-413">{testimonial.role}</p>
                  </div>
                </Card>)}
          </div>
        </div>
      </div>
    </section>;
}